import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { ShoppingBag, Package, IndianRupee, Plus, X, Tag, ClipboardList, Wallet, UserCircle, ShieldCheck } from "lucide-react";
import { Stat, NotPartner } from "./VetDashboard";
import QuickActions from "../components/QuickActions";

const CATS = ["food", "toy", "health", "grooming"];

const STORE_QUICK_ACTIONS = [
  { to: "/app/store/orders",   icon: ClipboardList, label: "All orders",     desc: "Accept · pack · dispatch",   color: "bg-brand/15 text-brand" },
  { to: "/app/store/products", icon: Tag,            label: "Catalogue",      desc: "Add · edit · stock",         color: "bg-sky/15 text-sky" },
  { to: "/app/payout-setup",   icon: Wallet,         label: "Payouts setup",  desc: "Bank · UPI · auto-settle",   color: "bg-leaf/30 text-ink" },
  { to: "/app/profile",        icon: UserCircle,     label: "Edit profile",   desc: "Name · city · photo",        color: "bg-sun/40 text-ink" },
  { to: "/app/kyc",            icon: ShieldCheck,    label: "KYC",            desc: "Verify your business",       color: "bg-ink/10 text-ink" },
];

export default function StoreDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "food", price: 499, photo: "", description: "", stock: 10 });

  const load = () => axios.get(`${API}/partner/store/dashboard`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  const addProduct = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/partner/store/products`, { ...form, price: Number(form.price), stock: Number(form.stock) });
    setOpen(false);
    setForm({ name: "", category: "food", price: 499, photo: "", description: "", stock: 10 });
    load();
  };

  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <NotPartner />;

  const { profile, stats, products, recent_orders } = data;

  return (
    <div className="space-y-6" data-testid="store-dashboard-page">
      <header className="rounded-3xl bg-gradient-to-br from-brand to-brand-hover text-white p-6 md:p-8 relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-wider font-bold opacity-80">Pet Store Dashboard</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Hey {user?.name?.split(" ")[0]}, here's your summary.</h1>
          <div className="opacity-90 text-sm">{profile.name} · {profile.city}</div>
        </div>
        <ShoppingBag className="absolute right-6 top-6 w-16 h-16 opacity-20"/>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Tag} label="Active listings" value={stats.active_listings} color="bg-brand/15 text-brand"/>
        <Stat icon={Package} label="Orders" value={stats.orders} color="bg-sky/15 text-sky"/>
        <Stat icon={ShoppingBag} label="Units sold" value={stats.units_sold} color="bg-leaf/30 text-ink"/>
        <Stat icon={IndianRupee} label="Revenue (delivered)" value={`₹${stats.revenue_this_month}`} color="bg-sunshine/30 text-ink"/>
      </div>

      <QuickActions items={STORE_QUICK_ACTIONS} testid="store-quick-actions"/>

      {stats.status_counts && (
        <section data-testid="store-status-breakdown" className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-heading text-lg font-bold">Order pipeline</h2>
            <a href="/app/store/orders" data-testid="open-orders" className="text-xs font-bold text-brand hover:underline">Manage all orders →</a>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
            {Object.entries(stats.status_counts).map(([s, n]) => (
              <div key={s} className={`rounded-2xl p-3 text-center border ${n > 0 ? "bg-surface-muted/60 border-line" : "bg-bg/40 border-line/50 opacity-60"}`}>
                <div className="font-heading text-xl font-extrabold">{n}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft mt-0.5">{s}</div>
              </div>
            ))}
          </div>
          {stats.pending_revenue > 0 && (
            <div className="mt-3 text-xs text-ink-soft">Pending revenue (orders not yet delivered): <b className="text-ink">₹{stats.pending_revenue}</b></div>
          )}
        </section>
      )}

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="store-products">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Your product listings</h2>
          <button data-testid="add-product-button" onClick={() => setOpen(true)} className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2 hover:bg-brand">
            <Plus className="w-4 h-4"/> Add product
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.product_id} data-testid={`store-product-${p.product_id}`} className="bg-surface-muted rounded-2xl overflow-hidden border border-line">
              <img src={p.photo} alt={p.name} className="w-full h-28 object-cover"/>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft">{p.category}</div>
                <div className="font-bold text-sm line-clamp-2">{p.name}</div>
                <div className="mt-1 flex justify-between text-sm">
                  <span className="font-heading font-extrabold">₹{p.price}</span>
                  <span className="text-ink-soft">Stock: {p.stock}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Recent orders</h2>
        {recent_orders.length === 0 && <div className="text-ink-soft text-sm">No orders yet.</div>}
        <div className="divide-y divide-line">
          {recent_orders.map(o => (
            <div key={o.order_id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-bold">Order {o.order_id.slice(-8)}</div>
                <div className="text-ink-soft text-xs">{new Date(o.created_at).toLocaleString()} · {o.items.length} item(s)</div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider bg-leaf/30 rounded-full px-2 py-1">{o.status}</span>
            </div>
          ))}
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={addProduct} onClick={e=>e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="add-product-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-extrabold">Add new product</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <Field label="Name"><input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="pinput" data-testid="prod-name"/></Field>
              <Field label="Category">
                <select value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="pinput" data-testid="prod-category">
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Price (₹)"><input type="number" required value={form.price} onChange={e=>setForm({...form, price:e.target.value})} className="pinput" data-testid="prod-price"/></Field>
              <Field label="Photo URL (optional)"><input value={form.photo} onChange={e=>setForm({...form, photo:e.target.value})} className="pinput" placeholder="https://…"/></Field>
              <Field label="Description"><input value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="pinput"/></Field>
              <Field label="Stock"><input type="number" required value={form.stock} onChange={e=>setForm({...form, stock:e.target.value})} className="pinput"/></Field>
            </div>
            <button data-testid="prod-save" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover">Save product</button>
          </form>
        </div>
      )}

      <style>{`.pinput{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.pinput:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
